import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { AppState, AppTheme, ProfileReducerState, AttendanceInterface, AttendanceReducerState, AttendancesReducerState } from "../../../interfaces";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import {
  Button,
  Grid,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  InputAdornment
} from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { DialogModal, ErrorView } from "../../../components";
import { clearAttendanceError, getAttendances, deleteAttendance } from "../../../store/actions";
import { checkUserAccess } from "../../../util/permission-checker";
import PopupMenuDialog from "../../../components/PopupMenuDialog";
import dateformat from "dateformat";
import UpsertAttendanceDetailsModal from "./Modals/UpsertAttendanceDetailsDialog";
import UserAttendanceDialog from "./Modals/UserAttendanceDialog";

interface AttendancesProps {
  attendances: AttendancesReducerState;
  attendance: AttendanceReducerState;
  profile: ProfileReducerState;
  appTheme: AppTheme;
  getAttendances: (search: string, page: number, per_page: number) => void;
  clearAttendanceError: () => void;
  deleteAttendance: (_id: string) => void;
}

const useStyles = (appTheme: AppTheme) =>
  makeStyles((theme: Theme) =>
    createStyles({
      root: {
        flexGrow: 1,
        width: "100%"
      },
      paper: {
        backgroundColor: appTheme.section.backgroundColor,
        width: "100%",
        padding: 30,
        marginTop: 20,
        marginBottom: 20
      },
      icon: {
        color: theme.palette.text.primary,
        padding: 10,
        borderRadius: 30,
        backgroundColor: appTheme.iconBackgroundColor,
        "&:hover": {
          backgroundColor: appTheme.iconBackgroundHoverColor
        }
      },
      searchButton: {
        marginRight: "-21px",
        height: "55px",
        borderRadius: "30px",
        width: "100px"
      },
      textInput: {
        marginTop: "5px",
        width: "300px"
      },
      textInputControl: {
        borderRadius: "30px",
        backgroundColor: appTheme.section.backgroundColor
      },
      container: {
        minHeight: "60vh"
      },
      textInputIcon: {
        color: "#000"
      }
    })
  );

const Attendances: React.FC<AttendancesProps> = (props) => {
  const { appTheme, attendances, attendance, profile, getAttendances, deleteAttendance, clearAttendanceError } = props;

  const [search, setSearch] = useState<string>(attendances.search);
  const [page, setPage] = useState<number>(attendances.page);
  const [perPage, setPerPage] = useState<number>(attendances.per_page ?? 20);
  const [selectedRecord, setSelectedRecord] = useState<AttendanceInterface | null>(null);
  const [popUpDialogOpen, setPopUpDialogOpen] = useState<boolean>(false);

  const [upsertAttendanceDetailsModelOpen, setUpsertAttendanceDetailsModelOpen] = useState<boolean>(false);
  const [userAttendanceModelOpen, setUserAttendanceModelOpen] = useState<boolean>(false);

  const popUpMenuItems = useMemo(() => {
    return [
      { text: "View User Attendances", permission: "view-user-attendances" },
      { text: "Update Attendance Details", permission: "edit-attendance" },
      { text: "Delete Attendance", permission: "delete-attendance" }
    ];
  }, []);

  const [menuItems, setMenuItems] = useState<Array<string>>(
    popUpMenuItems.filter((menuItem) => checkUserAccess(menuItem.permission, profile.permissions)).map((menuItem) => menuItem.text)
  );

  useEffect(() => {
    setMenuItems(popUpMenuItems.filter((menuItem) => checkUserAccess(menuItem.permission, profile.permissions)).map((menuItem) => menuItem.text));
  }, [popUpMenuItems, profile.permissions]);

  useEffect(() => {
    getAttendances(search, page, perPage);
  }, [search, page, perPage, getAttendances]);

  const styles = useStyles(appTheme)();

  type DialogOptions = {
    open: boolean;
    body: string;
  };

  const [dialogOptions, setDialogOptions] = useState<DialogOptions>({ open: false, body: "" });

  const dismissDialogModal = useCallback(() => {
    setDialogOptions({ open: false, body: "" });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => {
      clearAttendanceError();
    };
  }, [clearAttendanceError]);

  useEffect(() => {
    if (userAttendanceModelOpen) {
      return;
    }

    if (!attendance.error && attendance.response) {
      if (attendance.response.data.status === "Ok") {
        clearAttendanceError();
        return;
      }
      clearAttendanceError();

      setSelectedRecord(null);

      setUpsertAttendanceDetailsModelOpen(false);

      setUserAttendanceModelOpen(false);

      setDialogOptions({ open: true, body: attendance.response.data.status });

      getAttendances("", 0, perPage);
    }
  }, [userAttendanceModelOpen, attendance.error, attendance.response, perPage, getAttendances, clearAttendanceError]);

  useEffect(() => {
    if (upsertAttendanceDetailsModelOpen || userAttendanceModelOpen) {
      return;
    }

    const requestData = attendance.error ? attendance : null;

    if (!requestData) {
      return;
    }

    if (requestData.error && requestData.error.data && requestData.error.status) {
      let body: string = requestData.error.data.status;

      if (requestData.error.status === 500) {
        body = requestData.error.data.content;
      }

      setDialogOptions({ open: true, body: body });
    } else {
      if (requestData.error && requestData.error.message) {
        setDialogOptions({ open: true, body: requestData.error.message });
      }
    }
  }, [attendance, userAttendanceModelOpen, upsertAttendanceDetailsModelOpen]);

  const onMenuItemSelected = (menuItem: string) => {
    setPopUpDialogOpen(false);

    if (!selectedRecord) {
      return;
    }

    switch (menuItem) {
      case "View User Attendances":
        setUserAttendanceModelOpen(true);
        break;
      case "Update Attendance Details":
        setUpsertAttendanceDetailsModelOpen(true);
        break;
      case "Delete Attendance":
        if (window.confirm("Would you like to delete this attendance ?")) {
          deleteAttendance(selectedRecord.attendance_id);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.root}>
      <DialogModal open={dialogOptions.open} body={dialogOptions.body} onDismissed={dismissDialogModal} />
      <UpsertAttendanceDetailsModal
        open={upsertAttendanceDetailsModelOpen}
        onDismissed={() => setUpsertAttendanceDetailsModelOpen(false)}
        attendanceData={selectedRecord}
      />
      <UserAttendanceDialog
        open={userAttendanceModelOpen}
        onDismissed={() => {
          setUserAttendanceModelOpen(false);
          getAttendances(search, page, perPage);
        }}
        attendanceData={selectedRecord}
      />
      <PopupMenuDialog
        open={popUpDialogOpen}
        onDismissed={() => setPopUpDialogOpen(false)}
        onMenuItemSelected={onMenuItemSelected}
        menuItems={menuItems}
      />
      <Container maxWidth="lg">
        {attendances.error ? (
          <ErrorView error={attendances.error} onRetry={() => getAttendances(search, page, perPage)} />
        ) : (
          <React.Fragment>
            <Paper className={styles.paper}>
              <Grid container justifyContent="space-between" spacing={0}>
                <Grid item>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      {(attendances.loading || attendance.loading) && (
                        <CircularProgress style={{ display: "block", margin: "auto" }} color="primary" />
                      )}
                    </Grid>
                    <Grid item>
                      <Typography variant="h3" color="primary">
                        Attendances
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <TextField
                    onChange={(event) => {
                      setSearch(event.currentTarget.value);
                    }}
                    error={false}
                    value={search}
                    variant="outlined"
                    placeholder="Search ..."
                    className={styles.textInput}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search className={styles.textInputIcon} />
                        </InputAdornment>
                      ),
                      classes: { root: styles.textInputControl }
                    }}
                  />
                </Grid>
              </Grid>
              <br />
              <TableContainer className={styles.container}>
                <Table stickyHeader aria-label="attendances">
                  <TableHead>
                    <TableRow>
                      {[
                        { label: "ID", minWidth: 100 },
                        { label: "Title", minWidth: 250 },
                        { label: "Attendance", minWidth: 150 },
                        { label: "Date", minWidth: 250 },
                        { label: "Has Temp Check", minWidth: 150 },
                        { label: "Options", minWidth: 100 }
                      ].map((column) => (
                        <TableCell key={column.label} align="center" style={{ minWidth: column.minWidth }}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attendances.data.map((attendance: AttendanceInterface) => {
                      const _id = attendance.attendance_id;
                      return (
                        <TableRow hover role="checkbox" tabIndex={-1} key={_id}>
                          {["attendance_id", "title", "attendance", "date", "requires_temp_check", "options"].map((column) => {
                            const key = `${_id}-${column}`;

                            const data = (attendance as unknown as { [key: string]: any })[column];
                            if (column === "options") {
                              if (menuItems.length === 0) return <TableCell key={key} align="center"></TableCell>;
                              return (
                                <TableCell key={key} align="center">
                                  <Button
                                    disabled={menuItems.length === 0}
                                    variant="outlined"
                                    onClick={() => {
                                      setSelectedRecord(attendance);
                                      setPopUpDialogOpen(true);
                                    }}>
                                    Options
                                  </Button>
                                </TableCell>
                              );
                            } else if (column === "date") {
                              return (
                                <TableCell key={key} align="center">
                                  {dateformat(data, "ddd mmm dd yyyy")}
                                </TableCell>
                              );
                            } else if (column === "requires_temp_check") {
                              return (
                                <TableCell key={key} align="center">
                                  {data ? "Yes" : "No"}
                                </TableCell>
                              );
                            } else {
                              return (
                                <TableCell key={key} align="center">
                                  {data}
                                </TableCell>
                              );
                            }
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid container justifyContent="space-between" style={{ margin: "0px 10px 0px 10px" }}>
                <Grid item>
                  <Button
                    disabled={!checkUserAccess("add-attendance", profile.permissions)}
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      setSelectedRecord(null);
                      setUpsertAttendanceDetailsModelOpen(true);
                    }}>
                    Add New Attendance
                  </Button>
                </Grid>
                <Grid item>
                  <TablePagination
                    component="div"
                    count={attendances.total}
                    page={page}
                    onPageChange={(_, page) => setPage(page)}
                    rowsPerPage={perPage}
                    rowsPerPageOptions={[20, 50, 100, 1000]}
                    onRowsPerPageChange={(event) => {
                      setPerPage(parseInt(event.target.value));
                      setPage(0);
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </React.Fragment>
        )}
      </Container>
    </div>
  );
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, void, AnyAction>) => {
  return {
    getAttendances: (search: string, page: number, per_page: number) => dispatch(getAttendances(search, page, per_page)),
    clearAttendanceError: () => dispatch(clearAttendanceError()),
    deleteAttendance: (_id: string) => dispatch(deleteAttendance(_id))
  };
};

const mapStateToProps = (state: AppState) => {
  return {
    attendances: state.attendances,
    attendance: state.attendance,
    profile: state.profile,
    appTheme: state.general.theme
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Attendances);
